/**
 * useFormGeminiAPI Hook
 *
 * Hook spécialisé pour les formulaires utilisant FormGeminiService
 * Garantit que seule la génération de formulaires est possible
 *
 * Responsabilités :
 * - Appels API FormGeminiService (formulaires uniquement)
 * - Gestion des erreurs API
 * - Gestion des quotas
 * - Retry logic
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { formGeminiService } from "@core/lib/ai/services";
import type { GeminiAttachedFile } from "@core/services/FileAttachmentService";
import type { PollSuggestion } from "@core/types/poll-suggestions";
import { handleError, logError } from "@core/lib/error-handling";
import { logger } from "@core/lib/logger";
import i18n from "@core/i18n";

export interface GeminiAPIResponse {
  success: boolean;
  data?: PollSuggestion;
  error?: string;
  errorType?: "quota" | "network" | "parsing" | "unknown";
}

export interface UseFormGeminiAPIOptions {
  onQuotaExceeded?: () => void;
  onNetworkError?: () => void;
  debug?: boolean;
}

export interface UseFormGeminiAPIReturn {
  isLoading: boolean;
  generatePoll: (
    userMessage: string,
    attachedFile?: GeminiAttachedFile | null,
  ) => Promise<GeminiAPIResponse>;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook spécialisé pour gérer les appels à l'API Gemini FORMULAIRE
 *
 * Ce hook utilise FormGeminiService qui garantit que seule la génération
 * de formulaires est possible, jamais de sondages de dates.
 */
export function useFormGeminiAPI(
  options: UseFormGeminiAPIOptions = {},
): UseFormGeminiAPIReturn {
  const { onQuotaExceeded, onNetworkError, debug = false } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stocker les callbacks dans des refs pour éviter les re-créations
  const onQuotaExceededRef = useRef(onQuotaExceeded);
  const onNetworkErrorRef = useRef(onNetworkError);

  useEffect(() => {
    onQuotaExceededRef.current = onQuotaExceeded;
    onNetworkErrorRef.current = onNetworkError;
  }, [onQuotaExceeded, onNetworkError]);

  /**
   * Génère un FORMULAIRE à partir d'un message utilisateur
   *
   * Note: pollType est toujours "form" pour ce hook spécialisé
   */
  const generatePoll = useCallback(
    async (
      userMessage: string,
      attachedFile?: GeminiAttachedFile | null,
    ): Promise<GeminiAPIResponse> => {
      const requestId = crypto.randomUUID();
      const timestamp = new Date().toISOString();
      const trimmedMessage = userMessage.trim();

      if (!trimmedMessage) {
        return {
          success: false,
          error: "Message vide",
          errorType: "unknown",
        };
      }

      setIsLoading(true);
      setError(null);

      if (debug) {
        logger.info("🤖 Appel API FormGeminiService", "api", {
          message: trimmedMessage,
        });
      }

      try {
        const pollResponse = await formGeminiService.generatePollFromText(
          trimmedMessage,
          "form",
          attachedFile ?? undefined,
        );

        if (pollResponse.success && pollResponse.data) {
          if (debug) {
            logger.info("✅ Réponse FormGeminiService reçue", "api", {
              type: pollResponse.data.type,
            });
          }

          setIsLoading(false);
          return {
            success: true,
            data: pollResponse.data as PollSuggestion,
          };
        }

        // Échec de la génération
        const errorType = detectErrorType(pollResponse.error);

        if (errorType === "quota") {
          onQuotaExceededRef.current?.();
        } else if (errorType === "network") {
          onNetworkErrorRef.current?.();
        }

        // Prioriser le message d'erreur du backend s'il est disponible pour les quotas/rate limits
        const errorMessage =
          errorType === "quota" && pollResponse.message
            ? pollResponse.message
            : getErrorMessage(errorType);
        setError(errorMessage);
        setIsLoading(false);

        if (debug) {
          logger.warn("⚠️ Échec génération formulaire", "api", {
            error: pollResponse.error,
            errorType,
          });
        }

        return {
          success: false,
          error: errorMessage,
          errorType,
        };
      } catch (error) {
        const processedError = handleError(
          error,
          {
            component: "useFormGeminiAPI",
            operation: "generatePoll",
          },
          "Erreur lors de l'appel API FormGeminiService",
        );

        logError(processedError, {
          component: "useFormGeminiAPI",
          operation: "generatePoll",
        });

        const errorType = detectErrorType(processedError.message);
        const errorMessage = getErrorMessage(errorType);

        if (errorType === "quota") {
          onQuotaExceededRef.current?.();
        } else if (errorType === "network") {
          onNetworkErrorRef.current?.();
        }

        setError(errorMessage);
        setIsLoading(false);

        return {
          success: false,
          error: errorMessage,
          errorType,
        };
      }
    },
    [debug],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    generatePoll,
    error,
    clearError,
  };
}

/**
 * Détecte le type d'erreur à partir du message
 */
function detectErrorType(
  errorMessage?: string,
): "quota" | "network" | "parsing" | "unknown" {
  if (!errorMessage) return "unknown";

  const message = errorMessage.toLowerCase();

  if (
    message.includes("quota") ||
    message.includes("rate limit") ||
    message.includes("limit")
  ) {
    return "quota";
  }

  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("connection")
  ) {
    return "network";
  }

  if (
    message.includes("parse") ||
    message.includes("json") ||
    message.includes("format")
  ) {
    return "parsing";
  }

  return "unknown";
}

/**
 * Retourne un message d'erreur user-friendly selon le type
 */
function getErrorMessage(
  errorType: "quota" | "network" | "parsing" | "unknown",
): string {
  switch (errorType) {
    case "quota":
      return i18n.t("aiError.quota", { ns: "forms" });

    case "network":
      return i18n.t("aiError.network", { ns: "forms" });

    case "parsing":
      return i18n.t("aiError.parsing", { ns: "forms" });

    case "unknown":
    default:
      return i18n.t("aiError.unknown", { ns: "forms" });
  }
}
